import { Redirect, Route, Switch } from 'react-router-dom';
import { IonReactRouter } from '@ionic/react-router';
import { IonRouterOutlet } from '@ionic/react';
import PageLayout from '../layouts/PageLayout/PageLayout';
import ConnectionError from '../components/ConnectionError/ConnectionError';
import LoggedInLayout from '../layouts/LoggedInLayout';
import Dashboards from '../pages/Dashboards/Dashboards';
import Login from '../pages/Login/Login';
import DatasetList from '../pages/DatasetList/DatasetList';
import EndpointSetting from '../pages/EndpointSetting/EndpointSetting';
import useRoutes from '../hooks/useRoutes';
import useAbortPollsOnNavigation from '../hooks/useAbortPollsOnNavigation';
import { consumePostLoginRedirect } from './utils/consumePostLoginRedirect';
import { storePostLoginRedirectIfNeeded } from './utils/storePostLoginRedirectIfNeeded';
import withDatasetRoute from '../hoc/withDatasetRoute';
import DatasetDetailFilter from '../pages/DatasetDetailFilter/DatasetDetailFilter';
import DatasetDetail from '../pages/DatasetDetail/DatasetDetail';
import DatasetExport from '../pages/DatasetExport/DatasetExport';
import DatasetPrint from '../pages/DatasetPrint/DatasetPrint';
import Form from '../pages/Form/Form';
import NotImplemented from '../pages/NotImplemented/NotImplemented';
import withPageContent from '../hoc/withPageContent';
import LayoutEditor from '../pages/LayoutEditor/LayoutEditor';
import EmbeddedApplication from '../pages/EmbeddedApplication/EmbeddedApplication';
import Guides from '../pages/Guides/Guides';
import ExternalApp from '../pages/ExternalApp/ExternalApp';

interface RouterProps {
  hasError: boolean;
  isPending: boolean;
  isLoggedIn: boolean;
}

// Extract HOC invocations to module scope so Route `component` prop is stable.
// Inline `withDatasetRoute(Component)` would create a new component each render,
// causing unnecessary remounts and state loss (e.g. fullscreenReference).
const NotImplementedWithLayout = withPageContent(NotImplemented);
const FormWithDatasetRoute = withDatasetRoute(Form);
const DatasetDetailFilterWithDatasetRoute =
  withDatasetRoute(DatasetDetailFilter);
const DatasetDetailWithDatasetRoute = withDatasetRoute(DatasetDetail);
const DatasetExportWithDatasetRoute = withDatasetRoute(DatasetExport);
const DatasetPrintWithDatasetRoute = withDatasetRoute(DatasetPrint);
const DatasetListWithDatasetRoute = withDatasetRoute(DatasetList);
const LayoutEditorWithDatasetRoute = withDatasetRoute(LayoutEditor);
const EmbeddedApplicationWithLayout = withPageContent(EmbeddedApplication);
const GuidesWithLayout = withPageContent(Guides);
const ExternalAppWithDatasetRoute = withDatasetRoute(ExternalApp);

const Router = ({ hasError, isPending, isLoggedIn }: RouterProps) => {
  const routes = useRoutes();
  useAbortPollsOnNavigation();

  if (hasError) {
    return (
      <PageLayout>
        <ConnectionError />
      </PageLayout>
    );
  }

  if (isPending) {
    return <PageLayout isLoading />;
  }

  if (isLoggedIn) {
    return (
      <LoggedInLayout>
        <Switch>
          <Route
            exact
            path={routes.home()}
            render={() => {
              const target = consumePostLoginRedirect();
              if (target) {
                return <Redirect to={target} />;
              }
              return <Redirect to={routes.dashboards()} />;
            }}
          />
          <Route exact path={routes.dashboards()} component={Dashboards} />
          {/* AIR-410 Průvodce hub: learning paths and every agenda guide. */}
          <Route exact path={routes.guides()} component={GuidesWithLayout} />

          {/* Persistent full-page standalone applications. */}
          <Route
            exact
            path={routes.relative.application()}
            component={EmbeddedApplicationWithLayout}
          />

          {/* fallback routes */}
          <Route
            path={routes.relative.notImplemented()}
            component={NotImplementedWithLayout}
          />

          {/* form routes */}
          <Route
            path={routes.relative.formAddAction()}
            component={FormWithDatasetRoute}
          />
          <Route
            path={routes.relative.formSubsetAddAction()}
            component={FormWithDatasetRoute}
          />
          <Route
            path={routes.relative.formSubsetCopyAction()}
            component={FormWithDatasetRoute}
          />
          <Route
            path={routes.relative.formSubsetEditAction()}
            component={FormWithDatasetRoute}
          />
          <Route
            path={routes.relative.formListAddAction()}
            component={FormWithDatasetRoute}
          />
          <Route
            path={routes.relative.formListCopyAction()}
            component={FormWithDatasetRoute}
          />
          <Route
            path={routes.relative.formListEditAction()}
            component={FormWithDatasetRoute}
          />

          {/* filter detail routes */}
          <Route
            path={routes.relative.datasetDetailMasterFilter()}
            component={DatasetDetailFilterWithDatasetRoute}
          />
          <Route
            path={routes.relative.subsetDetailSlaveFilter()}
            component={DatasetDetailFilterWithDatasetRoute}
          />

          {/* layout editor routes */}
          <Route
            path={routes.relative.layoutEditor()}
            component={LayoutEditorWithDatasetRoute}
          />

          {/* external app route (control action type 'app') */}
          {/* Stays before dataset list so /app is not swallowed by the generic dataset route. */}
          <Route
            path={routes.relative.datasetApp()}
            component={ExternalAppWithDatasetRoute}
          />

          {/* dataset routes */}
          {/* Export stays before dataset list so /export is not swallowed by the generic dataset route. */}
          <Route
            path={routes.relative.datasetExport()}
            component={DatasetExportWithDatasetRoute}
          />
          {/* Same ordering reason as export: /print must win over the generic dataset route. */}
          <Route
            path={routes.relative.datasetPrint()}
            component={DatasetPrintWithDatasetRoute}
          />
          <Route
            path={routes.relative.datasetDetailSubset()}
            component={DatasetDetailWithDatasetRoute}
          />
          <Route
            path={routes.relative.datasetDetail()}
            component={DatasetDetailWithDatasetRoute}
          />
          <Route
            path={routes.relative.datasetList()}
            component={DatasetListWithDatasetRoute}
          />

          {/* mobile app routes */}
          <Route
            exact
            path={routes.setEndpoint()}
            component={EndpointSetting}
          />

          <Redirect to={routes.dashboards()} />
        </Switch>
      </LoggedInLayout>
    );
  }

  return (
    <IonRouterOutlet id="main">
      <Switch>
        <Route exact path={routes.home()} component={Login} />
        <Route exact path={routes.setEndpoint()} component={EndpointSetting} />
        <Route
          render={({ location }) => {
            storePostLoginRedirectIfNeeded(
              location.pathname,
              location.search,
              routes.home(),
              routes.setEndpoint()
            );
            return <Redirect to={routes.home()} />;
          }}
        />
      </Switch>
    </IonRouterOutlet>
  );
};

const RouterWrapper = (props: RouterProps) => {
  return (
    <IonReactRouter>
      <Router {...props} />
    </IonReactRouter>
  );
};

export default RouterWrapper;
